import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Globe, ShoppingBag, Layers, MessageSquare, ExternalLink } from "lucide-react";
import LeadForm from "../components/LeadForm";
import PageHero from "../components/PageHero";

const HERO_IMG = "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1800&q=80";

const svcIcons = [Globe, ShoppingBag, Layers, MessageSquare];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function DesarrolloPage() {
  const { t } = useTranslation();
  const services = t("desarrollo.services.items", { returnObjects: true });
  const steps = t("desarrollo.process.items", { returnObjects: true });
  const techs = t("desarrollo.tech.items", { returnObjects: true });

  return (
    <main>
      <PageHero
        eyebrow={t("desarrollo.hero.eyebrow")}
        title={t("desarrollo.hero.title")}
        subtitle={t("desarrollo.hero.subtitle")}
        image={HERO_IMG}
        imageAlt="Desarrollo de software con IA"
      />

      {/* Services */}
      <section className="section-y">
        <div className="container-page">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-12 max-w-2xl">
            {t("desarrollo.services.title")}
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-5">
            {services.map((s, i) => {
              const Icon = svcIcons[i] || Globe;
              const hasPartner = !!s.partnerUrl;
              // Alternate accent colors per card to balance orange + cyan
              const isCyan = i % 2 === 1;
              const accent = isCyan
                ? "text-dc-cyan-light dark:text-dc-cyan bg-dc-cyan-light/10 dark:bg-dc-cyan/15"
                : "text-dc-amber-light dark:text-dc-amber bg-dc-amber-light/10 dark:bg-dc-amber/15";
              return (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.07 }}
                  className="glass-card p-8 flex flex-col"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${accent}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed flex-1">{s.description}</p>

                  {hasPartner && (
                    <div className="mt-6 pt-5 border-t border-border">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
                        {s.partnerLabel}
                      </div>
                      <a
                        href={s.partnerUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="partner-deuvox-link"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border-2 border-dc-cyan-light/40 dark:border-dc-cyan/40 text-dc-cyan-light dark:text-dc-cyan hover:bg-dc-cyan-light dark:hover:bg-dc-cyan hover:text-white dark:hover:text-dc-navy hover:border-transparent transition-all"
                      >
                        {s.partnerName}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-y bg-secondary/30 border-y border-border">
        <div className="container-page">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-12">{t("desarrollo.process.title")}</motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="relative p-7 rounded-2xl border border-border bg-card/60">
                <div className={`text-5xl font-bold mb-4 ${i % 2 === 0 ? "text-dc-amber-light/30 dark:text-dc-amber/30" : "text-dc-cyan-light/30 dark:text-dc-cyan/30"}`}>
                  0{i + 1}
                </div>
                <h3 className="font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section className="section-y">
        <div className="container-page">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-10">{t("desarrollo.tech.title")}</motion.h2>
          <div className="flex flex-wrap gap-3">
            {techs.map((tech, i) => (
              <motion.span key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="px-5 py-3 rounded-full bg-secondary/60 border border-border text-sm font-medium hover:border-dc-cyan-light dark:hover:border-dc-cyan hover:text-dc-cyan-light dark:hover:text-dc-cyan transition-colors">
                {tech}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-y bg-secondary/30 border-t border-border">
        <div className="container-page max-w-3xl">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-3 text-center">Solicita tu cotización</motion.h2>
          <p className="text-center text-muted-foreground mb-10">
            Cuéntanos qué quieres construir. Respondemos con alcance, plazos y presupuesto.
          </p>
          <div className="glass-card p-8 md:p-10">
            <LeadForm pageOrigin="desarrollo" defaultService="desarrollo" />
          </div>
        </div>
      </section>
    </main>
  );
}
